import {Collapse} from 'bootstrap';
import GeneralUtility from '../../utilities/general-utility';
import PluginAction from '../plugin-action';

/**
 * Basic action for listing offers. Can be used as a base for actual list actions.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default abstract class AbstractListAction extends PluginAction {
    /**
     * Contains the filter elements.
     */
    protected filterContainer: HTMLElement;

    /**
     * Form for the search.
     */
    protected searchForm: HTMLFormElement;

    protected constructor(container: HTMLElement) {
        super(container);
        const filterContainer = this.container.querySelector<HTMLFormElement>('.filter');
        const searchForm = this.container.querySelector<HTMLFormElement>('form#filter');
        if (!filterContainer || !searchForm) {
            throw new Error('Required elements missing.');
        }

        this.filterContainer = filterContainer;
        this.searchForm = searchForm;
        this.initializeFilter();

        // Initialize certain stuff only when on the teacher checklist pages
        if (
            GeneralUtility.rootline.indexOf(GeneralUtility.pages.checklist.winter.checklist) !== -1 ||
            GeneralUtility.rootline.indexOf(GeneralUtility.pages.checklist.summer.checklist) !== -1
        ) {
            this.initializeInquiryModals();
            this.initializeNotesForms();
            this.initializeWishlist();

            // If a checklist step has a selected offer, move the selected offer rendered by this plugin to the checklist step placeholder. At this point, all
            // inquiry modal, note and wishlist logic is already applied to the hidden selected offer as well.
            const selectedOffer = this.container.querySelector('#selectedOffer');
            const checklistStepSelectedOffer = document.querySelector('#checklistStepSelectedOffer');
            if (selectedOffer && checklistStepSelectedOffer) {
                checklistStepSelectedOffer.append(selectedOffer);
                selectedOffer.classList.remove('d-none');
            }
        }
    }

    /**
     * Initializes the search filter.
     */
    protected initializeFilter(): void {
        const filterCollapsible = this.container.querySelector<HTMLElement>('#collapseFilter');
        const button = this.container.querySelector<HTMLElement>('[data-bs-target="#collapseFilter"]');
        if (!filterCollapsible || !button) {
            return;
        }
        const openText = button.querySelector<HTMLElement>('.text-open');
        const closeText = button.querySelector<HTMLElement>('.text-close');
        if (!openText || !closeText) {
            return;
        }

        // Setup show/hide more filters button
        filterCollapsible.addEventListener('show.bs.collapse', () => {
            openText.classList.remove('d-inline-block');
            openText.classList.add('d-none');
            closeText.classList.remove('d-none');
            closeText.classList.add('d-inline-block');
        });

        filterCollapsible.addEventListener('hide.bs.collapse', () => {
            openText.classList.remove('d-none');
            openText.classList.add('d-inline-block');
            closeText.classList.remove('d-inline-block');
            closeText.classList.add('d-none');
        });

        // If a filter is set within the collapsible, automatically open it
        let hasValues = false;
        filterCollapsible.querySelectorAll<HTMLInputElement>('input').forEach(field => {
            if (field.getAttribute('type') === 'checkbox' && field.checked) {
                hasValues = true;
            } else if (field.getAttribute('type') === 'text') {
                hasValues = field.value.trim() !== '';
            }
        });

        if (hasValues) {
            const collapse = Collapse.getOrCreateInstance(filterCollapsible);
            collapse.show();
        }
    }

    /**
     * Initializes loading content of inquiry modals on demand.
     */
    protected initializeInquiryModals(): void {
        this.container.querySelectorAll<HTMLElement>('.inquiry-modal').forEach(modal => {
            const modalContainer = this.container.querySelector<HTMLElement>('#' + modal.getAttribute('id'));
            modalContainer?.addEventListener('show.bs.modal', event => {
                const target = event.target as HTMLElement;
                const iframe = target.querySelector<HTMLIFrameElement>('iframe');
                if (iframe && iframe.dataset.src && target.dataset.checklist && target.dataset.offerId && target.dataset.offerType) {
                    let src = iframe.dataset.src;
                    src += '?checklist=' + parseInt(target.dataset.checklist);
                    src += '&offerId=' + parseInt(target.dataset.offerId);
                    src += '&offerType=' + target.dataset.offerType;
                    iframe.src = src;
                }
            });
        });
    }

    /**
     * Initializes AJAX saving of notes forms.
     */
    protected initializeNotesForms(): void {
        // Each offer entry has its own note form
        this.container.querySelectorAll<HTMLFormElement>('.notes-form').forEach(notesForm => {
            notesForm.querySelector<HTMLElement>('.save-notes')?.addEventListener('click', event => {
                const offerElement = notesForm.closest<HTMLElement>('.offer');
                const notesElement = notesForm.querySelector<HTMLTextAreaElement>('textarea');
                if (!offerElement || !notesElement || !notesElement.value) {
                    return;
                }

                fetch(GeneralUtility.getUrl() + '?offer-api[endpoint][]=checklist&offer-api[endpoint][]=saveOfferNotes&checklist=' + offerElement.dataset.checklist + '&offer=' + offerElement.dataset.offerId + '&offerType=' + offerElement.dataset.offerType + '&notes=' + notesElement.value + '&rand=' + Math.random())
                    .then(response => {
                        if (response.status !== 200) {
                            throw new Error(response.status + ' ' + response.statusText);
                        }
                        return response.text();
                    })
                    .then(() => {
                        const alertTemplate = notesForm.querySelector<HTMLElement>('.alert-template');
                        const alertPasted = notesForm.querySelector<HTMLElement>('.alert-pasted');
                        const buttonNotesEdit = offerElement.querySelector<HTMLElement>('.button-notes-edit');
                        const buttonNotesAdd = offerElement.querySelector<HTMLElement>('.button-notes-add');
                        if (!alertTemplate || !buttonNotesEdit || !buttonNotesAdd) {
                            return;
                        }

                        if (!alertPasted) {
                            alertTemplate.insertAdjacentHTML('beforebegin', alertTemplate.innerHTML);
                            // Select alert-success after we have re-injected it into the DOM
                            notesForm.querySelector<HTMLElement>('.alert-success')?.classList.add('show', 'alert-pasted');
                        } else {
                            notesForm.querySelector<HTMLElement>('.alert-success')?.classList.add('show');
                        }

                        buttonNotesEdit.classList.remove('d-none');
                        buttonNotesAdd.classList.add('d-none');
                    });

                event.preventDefault();
                return false;
            });
        });
    }

    /**
     * Updates all wishlist buttons with a new count.
     *
     * @param {number} count New wishlist count
     */
    protected updateWishlistButtons(count: number): void {
        // The fields targeted here are mostly found outside the offer list action, e.g. the wishlist-button in the checklist-step. We can safely target these
        // global fields here directly, without having to worry about writing stuff multiple times into the same field.
        const buttonWishlistEmpty = document.querySelector<HTMLElement>('#button-wishlist-empty');
        const buttonWishlist = document.querySelector<HTMLElement>('#button-wishlist');
        const tabLinkWishlist = document.querySelector<HTMLElement>('#tab-link-wishlist');
        const buttonCount = document.querySelector<HTMLElement>('#button-wishlist .count');
        const tabCount = document.querySelector<HTMLElement>('#tab-link-wishlist .count');
        if (!buttonWishlistEmpty || !buttonWishlist || !tabLinkWishlist || !buttonCount || !tabCount) {
            return;
        }

        if (count === 0) {
            buttonWishlistEmpty.classList.remove('d-none');
            buttonWishlist.classList.add('d-none');
            tabLinkWishlist.classList.add('disabled');
            buttonCount.innerHTML = '0';
            tabCount.innerHTML = '0';
        } else {
            buttonWishlistEmpty.classList.add('d-none');
            buttonWishlist.classList.remove('d-none');
            tabLinkWishlist.classList.remove('disabled');
            buttonCount.innerHTML = count.toString();
            tabCount.innerHTML = count.toString();
        }
    }

    /**
     * Initializes AJAX actions for wishlist.
     */
    protected initializeWishlist(): void {
        const offerList = this.container.querySelector<HTMLElement>('.offer-list');
        if (!offerList) {
            return;
        }

        const checklist = offerList.dataset.checklist;
        const offerType = offerList.dataset.offerType;

        // Get current wishlist status
        fetch(GeneralUtility.getUrl() + '?offer-api[endpoint][]=wishlist&offer-api[endpoint][]=status&checklist=' + checklist + '&offerType=' + offerType + '&rand=' + Math.random())
            .then(response => {
                if (response.status !== 200) {
                    throw new Error(response.status + ' ' + response.statusText);
                }
                return response.json();
            })
            .then(data => {
                this.updateWishlistButtons(parseInt(data.count));
            });

        // Performs the adding or removing from the wishlist
        const addOrRemoveFromWishlist = (button: HTMLButtonElement) => {
            GeneralUtility.hideAllTooltips();
            const checklist = button.dataset.checklist;
            const offer = button.dataset.offer;
            const offerType = button.dataset.offerType;
            const offerElement = button.closest<HTMLElement>('.offer');
            if (!checklist || !offer || !offerType || !offerElement) {
                return;
            }
            const buttonWishlistRemove = offerElement.querySelector('.button-wishlist-remove');
            const buttonWishlistAdd = offerElement.querySelector('.button-wishlist-add');
            if (!buttonWishlistRemove || !buttonWishlistAdd) {
                return;
            }
            const offerTabWishlist = button.closest<HTMLElement>('#offer-tab-wishlist');

            if (button.classList.contains('button-wishlist-add')) {
                fetch(GeneralUtility.getUrl() + '?offer-api[endpoint][]=wishlist&offer-api[endpoint][]=add&checklist=' + checklist + '&offerType=' + offerType + '&offer=' + offer + '&rand=' + Math.random())
                    .then(response => {
                        if (response.status !== 200) {
                            throw new Error(response.status + ' ' + response.statusText);
                        }
                        return response.json();
                    })
                    .then(data => {
                        buttonWishlistRemove.classList.remove('d-none');
                        buttonWishlistAdd.classList.add('d-none');
                        this.updateWishlistButtons(parseInt(data.count));
                    });
            } else {
                fetch(GeneralUtility.getUrl() + '?offer-api[endpoint][]=wishlist&offer-api[endpoint][]=remove&checklist=' + checklist + '&offerType=' + offerType + '&offer=' + offer + '&rand=' + Math.random())
                    .then((response: Response) => {
                        if (response.status !== 200) {
                            throw new Error(response.status + ' ' + response.statusText);
                        }
                        return response.json();
                    })
                    .then(data => {
                        buttonWishlistAdd.classList.remove('d-none');
                        buttonWishlistRemove.classList.add('d-none');
                        this.updateWishlistButtons(parseInt(data.count));

                        // If this is the wishlist list view, remove the item and show alert if list is empty
                        if (offerTabWishlist) {
                            if (parseInt(data.count) === 0) {
                                offerTabWishlist.querySelector('.alert-empty')?.classList.remove('d-none');
                            }
                            offerElement.remove();
                        }
                    });
            }
        };

        // Setup buttons to add offers to the wishlist
        this.container.querySelectorAll<HTMLButtonElement>('.button-wishlist-add').forEach(button => {
            button.addEventListener('click', (event) => {
                addOrRemoveFromWishlist(button);
                event.preventDefault();
                return false;
            });
        });

        // Setup buttons to remove offers to the wishlist
        this.container.querySelectorAll<HTMLButtonElement>('.button-wishlist-remove').forEach(button => {
            button.addEventListener('click', (event) => {
                addOrRemoveFromWishlist(button);
                event.preventDefault();
                return false;
            });
        });
    }
}
